/**
 * Light markdown renderer for SKILL.md bodies (web). Handles #/##/### headings,
 * - bullets, > quotes, **bold**, `code`, and paragraphs: neutral theme.
 */
import { useMemo, type CSSProperties, type ReactNode } from 'react';

interface Block {
  type: 'h1' | 'h2' | 'h3' | 'p' | 'ul' | 'quote';
  text?: string;
  items?: string[];
}

function parseBlocks(src: string): Block[] {
  const lines = src.replace(/\r/g, '').split('\n');
  const blocks: Block[] = [];
  let para: string[] = [];
  let list: string[] = [];
  const flushPara = () => {
    if (para.length) blocks.push({ type: 'p', text: para.join(' ') });
    para = [];
  };
  const flushList = () => {
    if (list.length) blocks.push({ type: 'ul', items: list });
    list = [];
  };
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (/^#\s+/.test(line)) { flushPara(); flushList(); blocks.push({ type: 'h1', text: line.replace(/^#\s+/, '') }); continue; }
    if (/^##\s+/.test(line)) { flushPara(); flushList(); blocks.push({ type: 'h2', text: line.replace(/^##\s+/, '') }); continue; }
    if (/^###\s+/.test(line)) { flushPara(); flushList(); blocks.push({ type: 'h3', text: line.replace(/^###\s+/, '') }); continue; }
    if (/^>\s?/.test(line)) { flushPara(); flushList(); blocks.push({ type: 'quote', text: line.replace(/^>\s?/, '') }); continue; }
    if (/^[-*]\s+/.test(line)) { flushPara(); list.push(line.replace(/^[-*]\s+/, '')); continue; }
    if (!line.trim()) { flushPara(); flushList(); continue; }
    flushList();
    para.push(line);
  }
  flushPara();
  flushList();
  return blocks.slice(0, 80);
}

function renderInline(text: string): ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).filter(Boolean);
  return parts.map((p, i) => {
    if (/^\*\*[^*]+\*\*$/.test(p)) return <strong key={i}>{p.slice(2, -2)}</strong>;
    if (/^`[^`]+`$/.test(p)) return <code key={i} style={code}>{p.slice(1, -1)}</code>;
    return <span key={i}>{p}</span>;
  });
}

export function SkillMarkdown({ source }: { source: string }) {
  const blocks = useMemo(() => parseBlocks(source), [source]);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {blocks.map((b, i) => {
        if (b.type === 'h1') return <h2 key={i} style={h1}>{b.text}</h2>;
        if (b.type === 'h2') return <h3 key={i} style={h2}>{b.text}</h3>;
        if (b.type === 'h3') return <h4 key={i} style={h3}>{b.text}</h4>;
        if (b.type === 'quote') return <blockquote key={i} style={quote} dir="auto">{renderInline(b.text!)}</blockquote>;
        if (b.type === 'ul')
          return (
            <ul key={i} style={{ margin: 0, paddingInlineStart: 20, display: 'flex', flexDirection: 'column', gap: 5 }}>
              {b.items!.map((it, j) => <li key={j} style={text} dir="auto">{renderInline(it)}</li>)}
            </ul>
          );
        return <p key={i} style={{ ...text, margin: 0 }} dir="auto">{renderInline(b.text!)}</p>;
      })}
    </div>
  );
}

const h1: CSSProperties = { margin: '6px 0 2px', fontSize: 19, fontWeight: 600, color: 'var(--text-primary)' };
const h2: CSSProperties = { margin: '4px 0 0', fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' };
const h3: CSSProperties = { margin: '4px 0 0', fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' };
const text: CSSProperties = { fontSize: 14, lineHeight: '22px', color: 'var(--text-secondary)' };
const quote: CSSProperties = { margin: 0, paddingInlineStart: 14, borderInlineStart: '3px solid var(--input-border)', fontSize: 14, lineHeight: '22px', color: 'var(--text-tertiary)', fontStyle: 'italic' };
const code: CSSProperties = { fontFamily: "'SF Mono', ui-monospace, Menlo, monospace", fontSize: 12.5, background: 'rgba(0,0,0,0.06)', padding: '1px 5px', borderRadius: 5 };
